import { useEffect, useState } from 'react';
import { trackAppInstalled } from './telemetry';

// Chrome/Android fire `beforeinstallprompt` once, early — capture it at module
// load so it isn't missed before any component mounts.
interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: InstallPromptEvent | null = null;
// Set when the browser reports an install (our button or its own menu).
let justInstalled = false;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach(l => l());

const DISMISS_KEY = 'bf_install_dismissed_at';
const DISMISS_DAYS = 14;

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as InstallPromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    justInstalled = true;
    trackAppInstalled();
    notify();
  });
}

export function isStandalone(): boolean {
  return window.matchMedia?.('(display-mode: standalone)').matches
    || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

// iOS has no install prompt API; users add via Share → Add to Home Screen.
export function isIos(): boolean {
  const ua = navigator.userAgent;
  // iPads report a desktop Mac user agent, so fall back to touch support.
  return /iphone|ipad|ipod/i.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

function recentlyDismissed(): boolean {
  try {
    const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return Date.now() - at < DISMISS_DAYS * 86_400_000;
  } catch {
    return false;
  }
}

export type InstallPlatform = 'android' | 'ios';

export function useInstallPrompt() {
  const [, rerender] = useState(0);
  useEffect(() => {
    const l = () => rerender(n => n + 1);
    listeners.add(l);
    return () => { listeners.delete(l); };
  }, []);

  // Whether this device can install at all (ignores "Not now"), for the
  // always-available button; `platform` also respects "Not now", for popups.
  const available: InstallPlatform | null =
    isStandalone() ? null
      : deferred ? 'android'
      : isIos() ? 'ios'
      : null;
  const platform = recentlyDismissed() ? null : available;

  return {
    platform,
    available,
    justInstalled,
    clearJustInstalled() {
      justInstalled = false;
      notify();
    },
    // Returns true if the user accepted the native prompt.
    async install(): Promise<boolean> {
      if (!deferred) return false;
      const e = deferred;
      deferred = null;
      await e.prompt();
      const { outcome } = await e.userChoice;
      notify();
      return outcome === 'accepted';
    },
    dismiss() {
      try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
      notify();
    },
  };
}

// Best effort: desktop Chrome opens the installed app by itself; on Android a
// link into the app's scope may open it, otherwise the home-screen icon does.
export function openInstalledApp() {
  window.open(`${window.location.origin}/?source=installed`, '_blank');
}
